import { Checkbox, Divider, FormGroup, Spacer, TextInput } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IOrthOV1DiagnosticReportCustomControlDataBevindingen } from '../../../../../common/validation/customControls/ophthalmology';
import type { IOrthOV1DiagnosticReportAddEditCustomControlFieldsProps } from './OrthOV1DiagnosticReportAddEditCustomControlFields';
import { orthODiagnosticReportCustomControlBevindingenLabels } from '../../../../../common/validation/customControls/ophthalmology';
import { Feedback } from '../../../elements/common';
import { FormValidationMessage } from '../../../elements/FormValidationMessage';

const bevindingenFirstColumn = [
    'refractieafwijking',
    'amblyopie',
    'strabismus',
    'diplopie',
    'accommodatie_problemen',
] as (keyof IOrthOV1DiagnosticReportCustomControlDataBevindingen)[];
const bevindingenSecondColumn = [
    'convergentie_insufficientie',
    'asthopene_klachten',
    'geen_afwijkingen',
] as (keyof IOrthOV1DiagnosticReportCustomControlDataBevindingen)[];

export function OrthOV1DiagnosticReportAddEditCustomControlDataBevindingen(
    props: IOrthOV1DiagnosticReportAddEditCustomControlFieldsProps
) {
    const [hasAndersTextError, setHasAndersTextError] = useState(false);
    const [hasBevindingenError, setHasBevindingenError] = useState(false);

    useEffect(() => {
        setHasBevindingenError(props.errors.has('customControlData.bevindingen'));
        setHasAndersTextError(props.errors.has('customControlData.bevindingen.andersText'));
    }, [props.errors]);

    return (
        <>
            <FormGroup
                hasErrors={hasBevindingenError || hasAndersTextError}
                isRequired
                label="Bevindingen"
            >
                <div className="row">
                    <div
                        className="col-sm-12"
                        style={{ minWidth: 240 }}
                    >
                        <Spacer spacing="small">
                            {bevindingenFirstColumn.map((name) => {
                                return (
                                    <Checkbox
                                        checked={!!props.customControlData.bevindingen[name]}
                                        className={`automation-bevindingen-${name}`}
                                        key={name}
                                        label={orthODiagnosticReportCustomControlBevindingenLabels[name]}
                                        onChange={async (event) => {
                                            await handleChangeBevindingen(name, event.target.checked);
                                        }}
                                    />
                                );
                            })}
                        </Spacer>
                    </div>
                    <div
                        className="col-sm-12"
                        style={{ minWidth: 240 }}
                    >
                        <Spacer
                            lastChildHasNoSpacing
                            spacing="small"
                        >
                            {bevindingenSecondColumn.map((name) => {
                                return (
                                    <Checkbox
                                        checked={!!props.customControlData.bevindingen[name]}
                                        className={`automation-bevindingen-${name}`}
                                        key={name}
                                        label={orthODiagnosticReportCustomControlBevindingenLabels[name]}
                                        onChange={async (event) => {
                                            await handleChangeBevindingen(name, event.target.checked);
                                        }}
                                    />
                                );
                            })}
                        </Spacer>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Checkbox
                                checked={props.customControlData.bevindingen.anders}
                                className="automation-bevindingen-anders"
                                onChange={async (event) => {
                                    await handleChangeBevindingen('anders', event.target.checked);
                                }}
                            />
                            <TextInput
                                className={`automation-bevindingen-anders ${
                                    props.errors.has('customControlData.bevindingen.andersText') ? 'has-danger' : ''
                                }`}
                                onChange={async (event) => {
                                    await handleChangeBevindingen('andersText', event.target.value);
                                    props.validateOneCustomControl('andersText');
                                }}
                                placeholder="Anders, namelijk..."
                                style={{ marginLeft: '12px' }}
                                value={props.customControlData.bevindingen.andersText || ''}
                            />
                        </div>
                        {hasAndersTextError && (
                            <Feedback error={props.errors.get('customControlData.bevindingen.andersText')} />
                        )}
                    </div>
                </div>
                <FormValidationMessage error={props.errors.get('customControlData.bevindingen')} />
            </FormGroup>

            <Divider />
        </>
    );

    async function handleChangeBevindingen<K extends keyof IOrthOV1DiagnosticReportCustomControlDataBevindingen>(
        name: K,
        value: IOrthOV1DiagnosticReportCustomControlDataBevindingen[K]
    ) {
        const updatedBevindingen = {
            ...props.customControlData.bevindingen,
            [name]: value,
        };

        if (name === 'andersText') {
            updatedBevindingen.anders = !!value;
        }

        if (name === 'anders' && !value) {
            updatedBevindingen.andersText = '';
        }

        await props.handleChangeCustomControl({
            bevindingen: updatedBevindingen,
        });

        props.validateOneCustomControl('bevindingen');
    }
}
